import React from "react";
import { bool } from "prop-types";

import Typography from "@components/shared/Typography";
import { useTranslation } from "@lib/i18n";
import { useDeliveryPrice } from "@hooks/finance-data/useDeliveryPrice";
import {
  useConfigurationSelector,
  useCurrencySettingsSelector,
} from "@redux/reducers/appConfig";
import { dealType } from "@types";
import { FG_CITROEN_REGISTRATION_KEY } from "@shared/constants";
import { getSolJourneyConfigForJourney } from "@shared/v2/lib/getSolJourneyConfigForJourney";
import { getIsLLDJourneySelected } from "@shared/v2/lib/getIsLLDJourneySelected";
import { getIsVACJourneySelected } from "@shared/v2/lib/getIsVACJourneySelected";
import { getIsB2BSelected } from "@shared/v2/lib/getIsB2BSelected";
import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";

const CitroenRegistrationPrice = ({ deal, withLabel = false }) => {
  const { t } = useTranslation();
  const configuration = useConfigurationSelector();
  const currency = useCurrencySettingsSelector();
  const monthlyPrice = useDeliveryPrice(FG_CITROEN_REGISTRATION_KEY);
  const { journey } = deal.financeSimulation;
  const registration = getSolJourneyConfigForJourney(
    journey,
    deal.businessModel,
    deal.configuration ?? configuration
  )?.vehicleRegistration?.citroenRegistration;

  if (!registration?.displayPrice || !registration?.price) {
    return null;
  }

  const isMonthly =
    (getIsLLDJourneySelected(journey) || getIsVACJourneySelected(journey)) &&
    monthlyPrice !== null;
  const price = (
    <Typography
      className="font-semibold whitespace-nowrap"
      data-id="registration-citroen-price"
    >
      +
      {getFormattedPrice(
        isMonthly ? monthlyPrice : registration.price,
        currency.format,
        currency.symbol
      )}
      {isMonthly && (
        <sup className="uppercase top-1">
          {getIsB2BSelected(deal.businessModel)
            ? t("price.b2b.month")
            : t("price.b2c.month")}
        </sup>
      )}
    </Typography>
  );

  return withLabel ? (
    <div className="flex items-center justify-between w-full gap-4 mt-4">
      <Typography>{t("customize.citroenRegistration")}</Typography>
      {price}
    </div>
  ) : (
    price
  );
};

CitroenRegistrationPrice.propTypes = {
  deal: dealType.isRequired,
  withLabel: bool,
};

export default CitroenRegistrationPrice;
